import React, { useMemo, useState } from 'react';
import {
  ACCESS_SETTING_LABELS,
  SECTION_TYPE_LABELS,
  VIEW_ACCESS_LABELS,
  type AccessSetting,
  type CommunityForum,
  type CommunityForumState,
  type SectionStatus,
  type SectionType,
  type ViewAccessSetting,
} from './types';
import { SECTION_ICONS } from './communityUtils';
import { useCommunityForum } from './CommunityForumContext';
import type { ForumInput } from './communityForumStore';
import { CommunityDevRowMeta, CommunityDevTh } from './CommunityDevUi';
import { entityRef, P } from './communityDevMode';
import CommunityChannelAccessModal from './CommunityChannelAccessModal';

function defaultHeaderId(state: CommunityForumState): string {
  return state.headers.find((h) => h.status === 'active')?.id ?? state.headers[0]?.id ?? '';
}

function emptyForum(state: CommunityForumState, headerId?: string): ForumInput {
  const hdr = headerId ?? defaultHeaderId(state);
  const count = state.forums.filter((f) => f.headerId === hdr).length;
  return {
    headerId: hdr,
    name: '',
    description: '',
    icon: 'fa-folder',
    type: 'regular',
    sortOrder: count + 1,
    status: 'active',
    createTopicsAccess: 'all',
    replyAccess: 'all',
    viewAccess: 'all',
  };
}

function forumToForm(forum: CommunityForum): ForumInput {
  return {
    headerId: forum.headerId,
    name: forum.name,
    description: forum.description,
    icon: forum.icon,
    type: forum.type,
    sortOrder: forum.sortOrder,
    status: forum.status,
    createTopicsAccess: forum.createTopicsAccess,
    replyAccess: forum.replyAccess,
    viewAccess: forum.viewAccess ?? 'all',
  };
}

export default function CommunityAdminForums() {
  const { state, addForum, editForum, removeForum, moveForums, getChannelAccessGrants } = useCommunityForum();
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ForumInput>(() => emptyForum(state));
  const [accessForum, setAccessForum] = useState<CommunityForum | null>(null);

  const headerTitle = (id: string) => state.headers.find((h) => h.id === id)?.title ?? '—';

  const forums = useMemo(() => {
    const list = [...state.forums].sort((a, b) => a.sortOrder - b.sortOrder);
    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter(
      (f) => f.name.toLowerCase().includes(q) || f.description.toLowerCase().includes(q),
    );
  }, [state.forums, search]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForum(state));
    setFormOpen(true);
  };

  const openEdit = (forum: CommunityForum) => {
    setEditingId(forum.id);
    setForm(forumToForm(forum));
    setFormOpen(true);
  };

  const saveForm = () => {
    if (!form.name.trim() || !form.headerId) return;
    if (editingId) editForum(editingId, form);
    else addForum(form);
    setFormOpen(false);
  };

  const moveUp = (id: string) => {
    const ordered = [...state.forums].sort((a, b) => a.sortOrder - b.sortOrder).map((f) => f.id);
    const idx = ordered.indexOf(id);
    if (idx <= 0) return;
    [ordered[idx - 1], ordered[idx]] = [ordered[idx], ordered[idx - 1]];
    moveForums(ordered);
  };

  const moveDown = (id: string) => {
    const ordered = [...state.forums].sort((a, b) => a.sortOrder - b.sortOrder).map((f) => f.id);
    const idx = ordered.indexOf(id);
    if (idx < 0 || idx >= ordered.length - 1) return;
    [ordered[idx], ordered[idx + 1]] = [ordered[idx + 1], ordered[idx]];
    moveForums(ordered);
  };

  const setField = <K extends keyof ForumInput>(key: K, value: ForumInput[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  if (state.headers.length === 0) {
    return (
      <p className="mt-6 text-sm text-slate-500">
        Сначала создайте хотя бы один заголовок на вкладке «Заголовки».
      </p>
    );
  }

  return (
    <div className="mt-6">
      <div className="dcf-toolbar">
        <button type="button" className="dcf-btn dcf-btn--primary" onClick={openCreate}>
          <i className="fas fa-plus" aria-hidden="true" />
          Создать форум
        </button>
        <input
          className="dcf-input dcf-search"
          placeholder="Поиск по форумам"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="dcf-admin-table-wrap mt-4">
        <table className="dcf-admin-table">
          <thead>
            <tr>
              <CommunityDevTh label="Название" storagePath={P.forumName} />
              <CommunityDevTh label="Заголовок" storagePath={P.forumHeaderId} />
              <CommunityDevTh label="Тип" storagePath={P.forumType} />
              <th>Видимость</th>
              <th>Статус</th>
              <th>Тем</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {forums.map((forum) => (
              <tr key={forum.id}>
                <td>
                  <div className="font-semibold text-slate-800">{forum.name}</div>
                  <div className="text-xs text-slate-500 mt-1 max-w-xs">{forum.description}</div>
                  <CommunityDevRowMeta parts={[entityRef(P.forums, forum.id), P.forumDescription]} />
                </td>
                <td>{headerTitle(forum.headerId)}</td>
                <td>{SECTION_TYPE_LABELS[forum.type]}</td>
                <td>
                  <span className="dcf-status-pill dcf-status-pill--muted">
                    {VIEW_ACCESS_LABELS[forum.viewAccess ?? 'all']}
                  </span>
                  {(forum.viewAccess ?? 'all') === 'invite' && (
                    <div className="text-xs text-slate-500 mt-1">
                      {getChannelAccessGrants('forum', forum.id).length} с доступом
                    </div>
                  )}
                </td>
                <td>
                  <span className={`dcf-status-pill ${forum.status === 'active' ? 'dcf-status-pill--active' : 'dcf-status-pill--hidden'}`}>
                    {forum.status === 'active' ? 'Активен' : 'Скрыт'}
                  </span>
                </td>
                <td>{forum.topicCount ?? 0}</td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    <button type="button" className="dcf-btn dcf-btn--ghost text-xs" onClick={() => moveUp(forum.id)}>↑</button>
                    <button type="button" className="dcf-btn dcf-btn--ghost text-xs" onClick={() => moveDown(forum.id)}>↓</button>
                    <button type="button" className="dcf-btn dcf-btn--ghost text-xs" onClick={() => openEdit(forum)}>Редактировать</button>
                    {(forum.viewAccess ?? 'all') === 'invite' && (
                      <button
                        type="button"
                        className="dcf-btn dcf-btn--ghost text-xs"
                        onClick={() => setAccessForum(forum)}
                      >
                        Доступ
                      </button>
                    )}
                    <button
                      type="button"
                      className="dcf-btn dcf-btn--ghost text-xs"
                      onClick={() => editForum(forum.id, { status: forum.status === 'active' ? 'hidden' : 'active' })}
                    >
                      {forum.status === 'active' ? 'Скрыть' : 'Показать'}
                    </button>
                    <button
                      type="button"
                      className="dcf-btn dcf-btn--ghost dcf-btn--danger text-xs"
                      onClick={() => {
                        if (window.confirm(`Удалить форум «${forum.name}» и все его темы?`)) {
                          removeForum(forum.id);
                        }
                      }}
                    >
                      Удалить
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {formOpen && (
        <div className="dcf-modal-overlay" onClick={() => setFormOpen(false)} role="presentation">
          <div className="dcf-modal dcf-modal--wide" onClick={(e) => e.stopPropagation()} role="dialog">
            <h2 className="dcf-modal__title">{editingId ? 'Редактировать форум' : 'Создать форум'}</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="dcf-field sm:col-span-2">
                <label className="dcf-field__label">Заголовок в сайдбаре</label>
                <CommunityDevRowMeta parts={[P.forumHeaderId]} />
                <select className="dcf-select" value={form.headerId} onChange={(e) => setField('headerId', e.target.value)}>
                  {state.headers.map((h) => (
                    <option key={h.id} value={h.id}>{h.title}</option>
                  ))}
                </select>
              </div>
              <div className="dcf-field sm:col-span-2">
                <label className="dcf-field__label">Название форума</label>
                <input className="dcf-input" value={form.name} onChange={(e) => setField('name', e.target.value)} />
              </div>
              <div className="dcf-field sm:col-span-2">
                <label className="dcf-field__label">Краткое описание</label>
                <textarea className="dcf-textarea" rows={3} value={form.description} onChange={(e) => setField('description', e.target.value)} />
              </div>
              <div className="dcf-field">
                <label className="dcf-field__label">Иконка</label>
                <select className="dcf-select" value={form.icon} onChange={(e) => setField('icon', e.target.value)}>
                  {SECTION_ICONS.map((icon) => (
                    <option key={icon} value={icon}>{icon.replace('fa-', '')}</option>
                  ))}
                </select>
              </div>
              <div className="dcf-field">
                <label className="dcf-field__label">Тип форума</label>
                <select className="dcf-select" value={form.type} onChange={(e) => setField('type', e.target.value as SectionType)}>
                  {(Object.keys(SECTION_TYPE_LABELS) as SectionType[]).map((t) => (
                    <option key={t} value={t}>{SECTION_TYPE_LABELS[t]}</option>
                  ))}
                </select>
              </div>
              <div className="dcf-field">
                <label className="dcf-field__label">Порядок</label>
                <input className="dcf-input" type="number" value={form.sortOrder} onChange={(e) => setField('sortOrder', Number(e.target.value) || 1)} />
              </div>
              <div className="dcf-field">
                <label className="dcf-field__label">Статус</label>
                <select className="dcf-select" value={form.status} onChange={(e) => setField('status', e.target.value as SectionStatus)}>
                  <option value="active">Активен</option>
                  <option value="hidden">Скрыт</option>
                </select>
              </div>
              <div className="dcf-field">
                <label className="dcf-field__label">Видимость</label>
                <select className="dcf-select" value={form.viewAccess} onChange={(e) => setField('viewAccess', e.target.value as ViewAccessSetting)}>
                  {(Object.keys(VIEW_ACCESS_LABELS) as ViewAccessSetting[]).map((k) => (
                    <option key={k} value={k}>{VIEW_ACCESS_LABELS[k]}</option>
                  ))}
                </select>
              </div>
              <div className="dcf-field">
                <label className="dcf-field__label">Кто может создавать темы</label>
                <select className="dcf-select" value={form.createTopicsAccess} onChange={(e) => setField('createTopicsAccess', e.target.value as AccessSetting)}>
                  {(Object.keys(ACCESS_SETTING_LABELS) as AccessSetting[]).map((k) => (
                    <option key={k} value={k}>{ACCESS_SETTING_LABELS[k]}</option>
                  ))}
                </select>
              </div>
              <div className="dcf-field">
                <label className="dcf-field__label">Кто может отвечать</label>
                <select className="dcf-select" value={form.replyAccess} onChange={(e) => setField('replyAccess', e.target.value as AccessSetting)}>
                  {(Object.keys(ACCESS_SETTING_LABELS) as AccessSetting[]).map((k) => (
                    <option key={k} value={k}>{ACCESS_SETTING_LABELS[k]}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="dcf-modal__actions">
              <button type="button" className="dcf-btn dcf-btn--secondary" onClick={() => setFormOpen(false)}>Отмена</button>
              {editingId && form.viewAccess === 'invite' && (
                <button
                  type="button"
                  className="dcf-btn dcf-btn--secondary"
                  onClick={() => {
                    const forum = state.forums.find((f) => f.id === editingId);
                    if (forum) setAccessForum(forum);
                  }}
                >
                  Управление доступом
                </button>
              )}
              <button type="button" className="dcf-btn dcf-btn--primary" onClick={saveForm}>Сохранить</button>
            </div>
          </div>
        </div>
      )}

      {accessForum && (
        <CommunityChannelAccessModal
          open
          channelKind="forum"
          channelId={accessForum.id}
          channelName={accessForum.name}
          onClose={() => setAccessForum(null)}
        />
      )}
    </div>
  );
}
